import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../utils/DebugLogger';
import { eventBus } from '../eventBus';
import { permissionsService } from '../services/permissionsService';
import { useCliente } from './useCliente';
import { useOcorrencias } from './useOcorrencias';
import { phoneFromFields, phoneToFields, phoneFromString } from './usePhoneFormat';
import { useCustomerInternationalConfig } from './useCustomerInternationalConfig';
import { useClienteFormSave } from './useClienteFormSave';
import { useClienteFormAddress } from './useClienteFormAddress';
import type { Clientes_GetCustomerByIdResponseDados } from '../contracts/Clientes_GetCustomerByIdResponse';

/**
 * Form data shape used by ClienteForm.
 *
 * Mirrors the Clientes_GetCustomerByIdResponseDados contract, but with the
 * datetime fields replaced by Date objects (Typed Data Layer — useCliente.fetchCliente
 * converts ISO 8601 UTC strings to Date instances at the composable boundary).
 *
 * `cliente_origem_id` and `cliente_status` are string-typed locally because the
 * UI binds them to <select-list> options (which use string IDs).
 */
type ClienteFormData = Omit<
  Clientes_GetCustomerByIdResponseDados,
  | 'cliente_data_cadastro'
  | 'cliente_data_ultimo_orcamento'
  | 'cliente_data_ultimo_compromisso'
  | 'cliente_data_ultimo_contato_fidelizacao'
  | 'cliente_origem_id'
  | 'cliente_status'
> & {
  cliente_data_cadastro: Date | null;
  cliente_data_ultimo_orcamento?: Date | null;
  cliente_data_ultimo_compromisso?: Date | null;
  cliente_data_ultimo_contato_fidelizacao?: Date | null;
  cliente_origem_id: string | number;
  cliente_status: string | number;
};

/**
 * Composable for ClienteForm business logic.
 * Orchestrates form state, loading, phone sync, and delegates
 * validation/save to useClienteFormSave and address/franchise
 * handling to useClienteFormAddress.
 *
 * @param {Object} options
 * @param {import('vue').Ref|import('vue').ComputedRef} options.clienteId - reactive client ID
 * @param {import('vue').Ref|import('vue').ComputedRef} options.prefill - reactive prefill data
 * @param {Function} options.emitFn - component emit function
 */
export function useClienteForm({ clienteId, prefill, pendingObservation, emitFn }: { clienteId?: number | null; prefill?: any; pendingObservation?: any; emitFn?: any }) {
  const { t } = useI18n();

  // --- Composables ---
  const intlConfig = useCustomerInternationalConfig();
  const {
    config: countryConfig, documentConfig, selectedDocumentType,
    currentDocumentPresentation, hasMultipleDocumentTypes,
    allowedDocumentOptions, isBrazil, phoneCountryCode,
    setDocumentConfig, setSelectedDocumentType, setCountryFromName,
    currentCountry
  } = intlConfig;

  const clienteApi = useCliente();
  const ocorrenciaApi = useOcorrencias();

  // --- Reactive state ---
  const formData = ref<ClienteFormData>(createEmptyFormData());
  const phoneModel = ref({ phoneNumber: '', countryCode: '', areaCode: '' });
  const origens = ref<any[]>([]);
  const locationSelectReady = ref(false);
  const isLoadingClientData = ref(false);
  const documentValidationError = ref('');
  const clienteCompletionData = ref({});

  log('ClienteForm', 'setup', 'phoneModel initialized:', phoneModel.value);

  const statusLista = ref([
    { ID: '0', Name: t('Inativo') },
    { ID: '1', Name: t('Ativo') },
    { ID: '2', Name: t('Arquivado') }
  ]);

  // --- Computed ---
  const language = computed(() => permissionsService.getCurrentLocale());
  const isBrazilianCountry = computed(() => isBrazil.value);
  const documentTypeOptions = computed(() =>
    allowedDocumentOptions.value.map(opt => ({ ID: opt.type, Name: opt.label }))
  );

  // --- Delegate modules ---
  const { validateNome, validateForm, saveCliente } = useClienteFormSave({
    t, formData,
    documentValidationError, currentDocumentPresentation,
    countryConfig, pendingObservation, prefill, emitFn,
    clienteApi: { createCliente: clienteApi.createCliente, updateCliente: clienteApi.updateCliente },
    ocorrenciaApi: { saveOcorrencia: ocorrenciaApi.saveOcorrencia }
  });

  const addressHandlers = useClienteFormAddress({
    t, formData, phoneModel, locationSelectReady,
    isBrazilianCountry, countryConfig,
    intlConfig: { setCountryFromName, setDocumentConfig, selectedDocumentType, setSelectedDocumentType },
    clienteApi: { consultaCep: clienteApi.consultaCep, fetchDocumentConfig: clienteApi.fetchDocumentConfig }
  });

  // --- Phone sync watcher ---
  watch(phoneModel, (newPhone) => {
    const fields = phoneToFields(newPhone);
    formData.value.cliente_ddi = fields.ddi;
    formData.value.cliente_ddd = fields.ddd;
    formData.value.cliente_telefone = fields.telefone;

    log('ClienteForm', 'phoneModel watcher', 'Updated formData:', {
      cliente_ddi: formData.value.cliente_ddi,
      cliente_ddd: formData.value.cliente_ddd,
      cliente_telefone: formData.value.cliente_telefone
    });
  }, { deep: true, immediate: true });

  // --- Loading methods ---

  async function loadOrigens() {
    try {
      origens.value = await clienteApi.fetchOrigens();
    } catch (error) {
      log('ClienteForm', 'loadOrigens', 'Error loading origens', error);
    }
  }

  async function loadInfos() {
    log('ClienteForm', 'loadInfos', 'Loading form data');
    try {
      await loadOrigens();
      log('ClienteForm', 'loadInfos', 'Form data loaded successfully');
    } catch (error) {
      log('ClienteForm', 'loadInfos', 'Error loading form data', error);
      eventBus.emit('globalMessage.error', t('Erro ao carregar dados do formulário'));
    }
  }

  async function initNewCliente() {
    formData.value.cliente_id = 0;
    formData.value.cliente_data_cadastro = new Date();
    formData.value.cliente_status = '1';

    if (origens.value.length > 0) {
      await nextTick();
      formData.value.cliente_origem_id = origens.value[0].ID;
    }

    applyPrefill();
    log('ClienteForm', 'loadCliente', 'New client initialized');
    emitFn('client-loaded', formData.value);
    eventBus.emit('cliente.form.ready', { mode: 'new' });
  }

  function applyPrefill() {
    if (!prefill.value) {
      return;
    }
    log('ClienteForm', 'loadCliente', 'Applying prefill data', prefill.value);

    if (prefill.value.name) {
      formData.value.cliente_nome = prefill.value.name;
    }
    if (prefill.value.phone) {
      const raw = prefill.value.phone.startsWith('+') ? prefill.value.phone : '+' + prefill.value.phone;
      phoneModel.value = phoneFromString(raw);
    }
  }

  async function loadExistingCliente(id: any) {
    const dados = await clienteApi.fetchCliente(id);

    // Set document config BEFORE formData so InputMask has the correct mask
    // when the CNPJ/RUT value is applied (prevents mask mismatch clearing the value)
    await applyDocConfigForEdit(dados);

    // Merge with empty defaults so any field absent from the API response
    // (Clientes_GetCustomerByIdResponseDados marks every field optional) keeps
    // the form's required-by-UI shape (cliente_origem_id/status as strings, etc.).
    formData.value = { ...createEmptyFormData(), ...dados };

    phoneModel.value = phoneFromFields({
      ddi: formData.value.cliente_ddi,
      ddd: formData.value.cliente_ddd,
      telefone: formData.value.cliente_telefone
    });

    autoSelectOriginIfEmpty();

    log('ClienteForm', 'loadCliente', 'Client loaded successfully');
    emitFn('client-loaded', formData.value);
    eventBus.emit('cliente.form.ready', { mode: 'edit', id });
  }

  function autoSelectOriginIfEmpty() {
    const val = formData.value.cliente_origem_id;
    if ((!val || val === '0') && origens.value.length > 0) {
      formData.value.cliente_origem_id = origens.value[0].ID;
    }
  }

  async function applyDocConfigForEdit(dados: any) {
    if (!dados.cliente_franquia_id) return;

    const docConfig = await clienteApi.fetchDocumentConfig(dados.cliente_franquia_id).catch(err => {
      log('ClienteForm', 'applyDocConfigForEdit', 'Error', err);
      return null;
    });
    if (!docConfig) return;

    setDocumentConfig(docConfig);
    const savedType = dados.cliente_document_type;
    if (savedType && docConfig.document_types?.includes(savedType)) {
      setSelectedDocumentType(savedType);
    }
  }

  async function loadCliente(id: any) {
    log('ClienteForm', 'loadCliente', 'Loading client', id);
    isLoadingClientData.value = true;

    try {
      if (id === 0) {
        await initNewCliente();
      } else {
        await loadExistingCliente(id);
      }
    } catch (error) {
      log('ClienteForm', 'loadCliente', 'Error loading client', error);
      eventBus.emit('globalMessage.error', t('Erro ao carregar cliente: ') + error.message);
    } finally {
      isLoadingClientData.value = false;
      await nextTick();
      locationSelectReady.value = true;
    }
  }

  // --- Document validation bridge ---
  function handleDocumentValidation(result: any) {
    documentValidationError.value = addressHandlers.handleDocumentValidation(result);
  }

  // --- AI Completion ---

  function retornaCliente() {
    return { ...formData.value, cliente_document_type: selectedDocumentType.value };
  }

  function extraInformationsObject() {
    return {
      status: { '1': 'Ativo' },
      instrucoes: [
        'cliente_data_cadastro é no formato ISO 8601 UTC com sufixo Z, exemplo: 2025-01-15T13:30:00Z',
        'se não souber alguma informação, deixe em branco'
      ]
    };
  }

  function inferSuccessCallback(result: any) {
    log('ClienteForm', 'inferSuccessCallback', 'AI completion successful', result);
    const fields = [
      'cliente_nome', 'cliente_cpf', 'cliente_email',
      'cliente_ddi', 'cliente_ddd', 'cliente_telefone',
      'cliente_cep', 'cliente_rua', 'cliente_numero',
      'cliente_complemento', 'cliente_bairro', 'cliente_obs',
      'cliente_location_id', 'cliente_location_type',
      'cliente_franquia_id', 'cliente_status', 'cliente_data_cadastro',
      'cliente_data_nascimento'
    ];
    for (const field of fields) {
      if (result[field]) {
        (formData.value as any)[field] = result[field];
      }
    }
  }

  function inferErrorCallback(error: any) {
    log('ClienteForm', 'inferErrorCallback', 'AI completion error', error);
    eventBus.emit('globalMessage.error', t('Erro ao inferir dados do cliente: ') + error);
  }

  async function init(id: any) {
    log('ClienteForm', 'mounted', 'Component mounted');
    await loadInfos();
    await loadCliente(id > 0 ? id : 0);
  }

  return {
    // State
    formData, phoneModel, origens, statusLista,
    clienteCompletionData, locationSelectReady,

    // Country / document config (passthrough)
    countryConfig, currentCountry, documentConfig,
    selectedDocumentType, currentDocumentPresentation,
    hasMultipleDocumentTypes, documentTypeOptions, phoneCountryCode,

    // Computed
    language,

    // Methods
    init, loadCliente, saveCliente, validateNome,
    handleCepInput: addressHandlers.handleCepInput,
    handleFranquiaData: addressHandlers.handleFranquiaData,
    handleFranquiaChange: addressHandlers.handleFranquiaChange,
    handleDocumentTypeChange: addressHandlers.handleDocumentTypeChange,
    handleDocumentValidation,

    // AI completion
    retornaCliente, extraInformationsObject,
    inferSuccessCallback, inferErrorCallback,
  };
}

function createEmptyFormData(): ClienteFormData {
  return {
    cliente_id: 0, cliente_nome: '', cliente_cpf: '',
    cliente_document_type: '', cliente_ddi: '', cliente_ddd: '',
    cliente_telefone: '', cliente_cep: '', cliente_rua: '',
    cliente_numero: '', cliente_complemento: '', cliente_bairro: '',
    cliente_email: '', cliente_obs: '', cliente_profissao: '', cliente_data_nascimento: '',
    cliente_location_id: undefined, cliente_location_type: '',
    cliente_franquia_id: undefined,
    cliente_status: '1', cliente_data_cadastro: null, cliente_origem_id: ''
  };
}
